import { definedMap } from "@glideapps/ts-necessities";
import type { PrimitiveValue } from "common/components/computation-types";
import { defined, isDefined } from "common/Support";
import type { WireActionWithTitle } from "common/wire/base-components";
import type { Unbound, WireEditableValue } from "common/wire/types";
import { UnboundVal } from "common/wire/types";
import type { WireBackend } from "webapp/hydrated-ui/types";

export interface ExtractedAction {
    run?: (handled: boolean) => void;
    title: string;
    url?: string;
    enabled: boolean;
}

export type ExtractedActions = readonly (ExtractedAction | undefined)[];

export function extractActions(
    actions: readonly WireActionWithTitle[] | Unbound | undefined,
    backend: WireBackend
): ExtractedActions {
    return (
        actions?.map(a => {
            if (a.action === UnboundVal || !isDefined(a.title)) return undefined;
            return {
                run:
                    a.action === undefined
                        ? undefined
                        : (handled: boolean) => backend.runAction(defined(a.action?.token), handled),
                title: a.title,
                url: a.action?.url ?? undefined,
                enabled: a.action !== undefined,
            };
        }) ?? []
    );
}

export function breakoutActions(
    actions: ExtractedActions | undefined,
    numSpecial: 1
): [ExtractedAction | undefined, ...ExtractedAction[]];
export function breakoutActions(
    actions: ExtractedActions | undefined,
    numSpecial: 2
): [ExtractedAction | undefined, ExtractedAction | undefined, ...ExtractedAction[]];
export function breakoutActions(
    actions: ExtractedActions | undefined,
    numSpecial: 3
): [ExtractedAction | undefined, ExtractedAction | undefined, ExtractedAction | undefined, ...ExtractedAction[]];
export function breakoutActions(
    actions: ExtractedActions | undefined,
    numSpecial: number
): (ExtractedAction | undefined)[] {
    if (actions === undefined) return [];
    const start = actions.slice(0, numSpecial);
    const end = actions.slice(numSpecial).filter(isDefined);
    return [...start, ...end];
}

export function useWireValue<T extends PrimitiveValue>(
    val: WireEditableValue<T>,
    backend: WireBackend
): [T, ((newVal: T) => void) | undefined] {
    return [val.value, definedMap(val.onChangeToken, x => (newVal: T) => backend.valueChanged(x, newVal))];
}
